/**
 * Routes Index
 * Combines all module routes and exports the AppRoutes component
 */

import { Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { RouteConfig, ModuleRoutes } from "./types";

// Import all module routes
import { vpnRoutes } from "./modules/vpn.routes";
import { rhRoutes } from "./modules/rh.routes";
import { terminaisRoutes } from "./modules/terminais.routes";
import { documentosRoutes } from "./modules/documentos.routes";
import { relatoriosRoutes } from "./modules/relatorios.routes";
import { maquinasRoutes } from "./modules/maquinas.routes";
import { infraestruturaRoutes } from "./modules/infraestrutura.routes";
import { segurancaRoutes } from "./modules/seguranca.routes";
import { lojasRoutes } from "./modules/lojas.routes";
import { substabelecidosRoutes } from "./modules/substabelecidos.routes";
import { adminRoutes, ajudaAdminRoutes } from "./modules/admin.routes";
import { webhooksRoutes } from "./modules/webhooks.routes";
import { apisRoutes } from "./modules/apis.routes";
import { ajudaRoutes } from "./modules/ajuda.routes";
import { coreRoutes } from "./modules/core.routes";
import { systemRoutes } from "./modules/system.routes";
import { notificationsRoutes } from "./modules/notifications.routes";
import { aparenciaRoutes } from "./modules/aparencia.routes";
import { loteseRoutes } from "./modules/lotese.routes";
import { salasRoutes } from "./modules/salas.routes";
import { cofreRoutes } from "./modules/cofre.routes";
import { filialRoutes } from "./modules/filial.routes";
import { atendimentosRoutes } from "./modules/atendimentos.routes";
import { chatRoutes } from "./modules/chat.routes";
import { filesRoutes } from "./modules/files.routes";
import { profileRoutes } from "./modules/profile.routes";
import { ticketsRoutes } from "./modules/tickets.routes";
import { tasksRoutes } from "./modules/tasks.routes";
import { projectsRoutes } from "./modules/projects.routes";
import { feedRoutes } from "./modules/feed.routes";
import { centralRoutes } from "./modules/central.routes";
import { metasRoutes } from "./modules/metas.routes";
import { auditEventsRoutes } from "./modules/audit-events.routes";
import { adminRbacRoutes } from "./modules/admin-rbac.routes";
import { adminHomeWidgetsRoutes } from "./modules/admin-home-widgets.routes";
import { adminAccessMapRoutes } from "./modules/admin-access-map.routes";
import { adminPapelRegraRoutes } from "./modules/admin-papel-regra.routes";
import { roteiroRoutes } from "./modules/roteiro.routes";
import { calendarRoutes } from "./modules/calendar.routes";
import { processDesignerRoutes } from "./modules/process-designer.routes";
import { publicacoesRoutes } from "./modules/publicacoes.routes";

// Loading fallback for lazy-loaded components
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

/**
 * All module routes combined
 */
export const allModuleRoutes: ModuleRoutes[] = [
  vpnRoutes,
  rhRoutes,
  terminaisRoutes,
  documentosRoutes,
  relatoriosRoutes,
  maquinasRoutes,
  infraestruturaRoutes,
  segurancaRoutes,
  lojasRoutes,
  substabelecidosRoutes,
  adminRoutes,
  ajudaAdminRoutes,
  webhooksRoutes,
  apisRoutes, // API Management, Tokens, Catalog, Logs
  ajudaRoutes,
  notificationsRoutes, // Notification Providers (Story 1.1)
  aparenciaRoutes, // Theme System - Palette Selection
  loteseRoutes, // Lotese Module - GES Points (Story 1.1)
  salasRoutes, // Agendamento de Salas — Epic E13 (Story E13.2.1)
  cofreRoutes, // Cofre Técnico — license + purchase account CRUD (Plan 2)
  filialRoutes, // Perfil da Filial — per-loja overview (Plan 3 follow-up)
  atendimentosRoutes, // Atendimento ao Parceiro — painel call-center 3 colunas (PRD 4, Story 4.1.4)
  ticketsRoutes, // Tickets/Chamados — list + detail (PRD 1, Story 1.1.5)
  tasksRoutes, // Tasks Kanban 2D — drag-drop com dnd-kit (PRD 1, Story 1.4.3)
  projectsRoutes, // Projetos + marcos + progresso (PRD 1, Story 1.4.5)
  feedRoutes, // Feed Social — PRD 2 timeline com WS realtime (Story 2.4.3)
  centralRoutes, // Central do Parceiro — E21.1.1 (/central operador↔setor)
  metasRoutes, // Metas mensais — E22 (/metas listagem + cadastro)
  auditEventsRoutes, // Audit Log — Expansão v2 E4.2.2 (/admin/audit-events)
  adminRbacRoutes, // RBAC Audit — Expansão v2 E5.1.3 (/admin/rbac-audit)
  adminHomeWidgetsRoutes, // Home Widgets config — E19 Fase 5 (/admin/home-widgets)
  adminAccessMapRoutes, // Mapa de Acesso — E1 Fase B (/admin/mapa-de-acesso)
  adminPapelRegraRoutes, // Regra de papel por critério — E1b Fase C (/admin/regra-de-papel)
  roteiroRoutes, // Roteiro — pendências, entregas e diário (/admin/roteiro)
  calendarRoutes, // Calendar — Expansão v2 E2.3.1 (FullCalendar 6 unificado)
  processDesignerRoutes, // Process Designer BPMN — E18.2.1 (bpmn-js lazy /process-designer)
  publicacoesRoutes, // Motor de Publicação — Story 3.1 (/publicacoes/ocasioes)
  chatRoutes, // Chat + Voice + Huddle — PRD 2 comunicação interna full UI
  filesRoutes, // Arquivos compartilhados
  profileRoutes, // /perfil + /busca/resultados
  systemRoutes, // Module Management & System X-Ray
  coreRoutes, // Core routes should be last (contains 404 catch-all)
];

/**
 * Renders a single route with optional permission protection
 */
const renderRoute = (route: RouteConfig, index: number) => {
  const element = route.permission ? (
    <RequirePermission resource={route.permission.resource} action={route.permission.action}>
      <Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>
    </RequirePermission>
  ) : (
    <Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>
  );

  return <Route key={`${route.path}-${index}`} path={route.path} element={element} />;
};

/**
 * Renders all routes from a module
 */
const renderModuleRoutes = (module: ModuleRoutes) => {
  return module.routes.map((route, index) => renderRoute(route, index));
};

/**
 * All protected routes as React Router elements
 * This component includes its own Routes wrapper
 */
export const AppRoutes = () => {
  return <Routes>{allModuleRoutes.map((module) => renderModuleRoutes(module))}</Routes>;
};

// Re-export types
export type { RouteConfig, ModuleRoutes, RoutePermission } from "./types";

// Re-export individual module routes for flexibility
export {
  vpnRoutes,
  rhRoutes,
  terminaisRoutes,
  documentosRoutes,
  relatoriosRoutes,
  maquinasRoutes,
  infraestruturaRoutes,
  segurancaRoutes,
  lojasRoutes,
  substabelecidosRoutes,
  adminRoutes,
  ajudaAdminRoutes,
  webhooksRoutes,
  ajudaRoutes,
  notificationsRoutes,
  systemRoutes,
  coreRoutes,
};
